import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  getImageComments,
  getOutputSurfaceBlobNameFromImageUrl,
  getOutputSurfaceBlobNames,
} from "../../../shared/utils/utils";
import { sendReviewReport } from "../../../services/users";
import {
  getAzureBlob,
  getDeletedSegmentations,
  getTemporalData,
  restoreSegmentation,
  updateOutputSurface,
  deleteSegmentation,
  propagateCategoryChange,
} from "../../../services/container";
import { formatAzureBlob } from "../../../components/formatted/FormattedAzureSasToken";
import { formattedColorMaterial } from "../../../components/formatted/FormattedText";
import {
  formattedDate,
  formattedTime,
} from "../../../components/formatted/FormattedDateAndTime";
import { showErrorToast, showSuccessToast } from "../../../utils/errorHandler";
import WasteToolbarThumbnail from "../../../components/imageCard/WasteToolbarThumbnail";
import { sortByImageUrl, sortImageUrls } from "../core/utils/utils";
import StatusPicker from "../../../components/StatusPicker";
import Cookies from "js-cookie";
import {
  LoadingOverlay,
  InlineLoader,
} from "../../../components/loader/CharacterizationLoader";
import EmptyState from "../../../components/EmptyState";
import { Card, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import StandardTable from "../../../ui/components/StandardTable";
import Pagination from "../../../components/utils/Pagination";
import StatusFilterModalRevision from "./StatusFilterModalRevision";
import TypeFilterModalRevision from "./TypeFilterModalRevision";
import WastePolygonModal from "../../../components/utils/WastePolygonModal";
import { getRevisionRowKey } from "./utils/getRevisionRowKey";

const getStatusOptions = (t) => [
  { value: "all", label: t("All") },
  { value: "pending", label: t("pending") },
  { value: "validated", label: t("Validated") },
  { value: "restored", label: t("Restored") },
  { value: "deleted", label: t("Deleted") },
  { value: "error", label: t("rejected") },
];

const getTypeOptions = (t) => [
  { value: "all", label: t("All") },
  { value: "comment", label: t("Commentaire") },
  { value: "category", label: t("Category") },
  { value: "deletion", label: t("Deletion") },
  { value: "restoration", label: t("Restoration") },
];

const PAGE_SIZE = 10;

const getImageUrlFromHistoryItem = (historyEntry) =>
  historyEntry?.image_url ||
  (Array.isArray(historyEntry?.image_urls) && historyEntry.image_urls.length > 0
    ? historyEntry.image_urls[0]
    : null);

const getContainerMeasurementId = (containerItem) =>
  containerItem?._measurement ?? containerItem?._id;

const getImageSideSuffix = (imageUrl) =>
  imageUrl?.match(/-(L|R)\./)?.[1] ?? null;

const getSuperContainerChildContainers = (superContainer, containers = []) => {
  const idList = superContainer?.containerObjectIdList?.filter(Boolean) ?? [];
  if (idList.length === 0) return [];

  return idList
    .map((id) => {
      const found = containers.find(
        (c) => c?._id === id || c?._measurement === id,
      );
      return found ?? { _id: id };
    })
    .sort(sortByImageUrl);
};

const resolveImageUrlForSuperContainer = (
  historyEntry,
  containerItem,
  index,
  sortedImageUrls,
) => {
  const containerSuffix = getImageSideSuffix(containerItem?.image_url);
  if (containerSuffix && sortedImageUrls.length > 0) {
    const matched = sortedImageUrls.find((url) =>
      url.includes(`-${containerSuffix}.`),
    );
    if (matched) return matched;
  }
  return sortedImageUrls[index] || getImageUrlFromHistoryItem(historyEntry);
};

const resolveSuperContainerImageUrl = async (
  historyEntry,
  containerItem,
  index,
  sortedImageUrls,
) => {
  const containerId = containerItem?._id;
  if (!containerId || !historyEntry?._time) return null;

  try {
    const response = await getTemporalData(
      { time: historyEntry._time },
      containerId,
    );
    if (response?.status === 200) {
      const temporal = response.data?.temporalData?.[0];
      if (temporal?.image_url) return temporal.image_url;
    }
  } catch {
    // repli ci-dessous
  }

  return resolveImageUrlForSuperContainer(
    historyEntry,
    containerItem,
    index,
    sortedImageUrls,
  );
};

const fetchRevisionsForSuperContainerCapture = async ({
  azureUrl,
  historyEntry,
  childContainers,
}) => {
  const time = historyEntry?._time;
  if (!time) return [];

  const sortedImageUrls = sortImageUrls([...(historyEntry.image_urls || [])]);

  const perContainer = await Promise.all(
    childContainers.map(async (containerItem, index) => {
      const imageUrl = await resolveSuperContainerImageUrl(
        historyEntry,
        containerItem,
        index,
        sortedImageUrls,
      );
      const measurementId = getContainerMeasurementId(containerItem);
      if (!imageUrl || !measurementId) return [];

      return fetchRevisionsForCapture({
        azureUrl,
        time,
        imageUrls: [imageUrl],
        getMeasurementId: () => measurementId,
      });
    }),
  );

  return perContainer.flat();
};

const fetchRevisionsForCapture = async ({
  azureUrl,
  time,
  imageUrls,
  fallbackImageUrl,
  getMeasurementId,
}) => {
  if (!time) return [];

  const urls =
    Array.isArray(imageUrls) && imageUrls.length > 0
      ? sortImageUrls([...imageUrls])
      : fallbackImageUrl
        ? [fallbackImageUrl]
        : [];

  if (urls.length === 0) return [];

  const entriesByUrl = await Promise.all(
    urls.map(async (imageUrl, index) => {
      const measurementId = getMeasurementId(imageUrl, index);
      if (!imageUrl || !measurementId) return [];

      const commentsData = await getImageComments(
        azureUrl,
        measurementId,
        imageUrl,
        time,
      );
      const comments = Array.isArray(commentsData) ? commentsData : [];

      const deletedBlobNames = getOutputSurfaceBlobNames(
        imageUrl,
        time,
        measurementId,
      );
      const deletedByBlob = await Promise.all(
        deletedBlobNames.map(async (blobName) => {
          try {
            const res = await getDeletedSegmentations({ blobName });
            return (res.data?.deletedSegmentations || []).map((seg) => ({
              ...seg,
              image_url: imageUrl,
              reviewStatus: "accepted",
              suggestedForDeletion: true,
              sourceBlobName: blobName,
            }));
          } catch {
            return [];
          }
        }),
      );
      const deleted = [
        ...new Map(
          deletedByBlob
            .flat()
            .map((seg) => [`${seg.id}-${seg.deletedAt || ""}`, seg]),
        ).values(),
      ];
      const revisionsById = new Map(
        comments.map((comment, index) => [
          comment.id ?? `comment-${index}`,
          comment,
        ]),
      );
      deleted.forEach((segmentation, index) => {
        const key = segmentation.id ?? `deleted-${index}`;
        const existing = revisionsById.get(key);
        const existingTime = Date.parse(existing?.reviewTime || "") || 0;
        const deletedTime = Date.parse(segmentation.deletedAt || "") || 0;
        if (!existing || deletedTime >= existingTime) {
          revisionsById.set(key, segmentation);
        }
      });

      return [...revisionsById.values()].map((entry) => ({
        ...entry,
        containerId: measurementId,
        captureTime: time,
      }));
    }),
  );

  return entriesByUrl.flat();
};

const formatDateOnly = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const TableHeaderCell = ({ label, columnKey, sortConfig, onSort }) => {
  const isActive = sortConfig.column === columnKey;
  const directionIcon = isActive
    ? sortConfig.direction === "asc"
      ? "fa-sort-up"
      : "fa-sort-down"
    : "fa-sort";
  return (
    <div
      className="table-standard-header-cell"
      style={{ cursor: "pointer" }}
      onClick={() => onSort(columnKey)}
      onKeyDown={(e) => e.key === "Enter" && onSort(columnKey)}
      role="button"
      tabIndex={0}
    >
      {label}
      <span className="sort-icon">
        <i className={`fas ${directionIcon}`} aria-hidden />
      </span>
    </div>
  );
};

const CharacterizationRevisionHistory = ({
  container,
  superContainer,
  containers = [],
  historyMaterial = [],
  onRevisionCountChange,
  refreshKey = 0,
}) => {
  const { t } = useTranslation();
  const azureUrl = process.env.REACT_APP_AZURE_URL;
  const stageRefs = useRef({});
  const [reviewComments, setReviewComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(PAGE_SIZE);
  const [resultPage, setResultPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    column: "date",
    direction: "desc",
  });
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [openModificationDropdownIndex, setOpenModificationDropdownIndex] =
    useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [selectedRevisionItem, setSelectedRevisionItem] = useState(null);
  const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);
  const [isRevisionModalLoading, setIsRevisionModalLoading] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [isSendReportModalOpen, setIsSendReportModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const modificationDropdownRef = useRef(null);
  const modificationDropdownPortalRef = useRef(null);

  useLayoutEffect(() => {
    if (openModificationDropdownIndex == null) {
      setDropdownPosition(null);
      return;
    }
    const update = () => {
      if (!modificationDropdownRef.current) return;
      const rect = modificationDropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        left: rect.left,
        bottom: window.innerHeight - rect.top - 40,
      });
    };
    update();
    const handleScroll = () => requestAnimationFrame(update);
    window.addEventListener("resize", update);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", update);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [openModificationDropdownIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inCell =
        modificationDropdownRef.current?.contains(event.target) ?? false;
      const inPortal =
        modificationDropdownPortalRef.current?.contains(event.target) ?? false;
      if (!inCell && !inPortal) {
        setOpenModificationDropdownIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setOpenModificationDropdownIndex(null);
  }, [currentPage]);

  const renderModificationDropdownContent = (item) => {
    const sections = [];
    const hasCategoryChange =
      Boolean(item.reviewCls) ||
      Boolean(item.oldCls && item.oldCls !== item.cls);
    if (hasCategoryChange) {
      const beforeCls = item.oldCls ?? item.cls ?? "";
      const afterCls = item.reviewCls || item.cls || "";
      sections.push({
        title: t("Category"),
        before: beforeCls ? t(beforeCls) : "-",
        after: afterCls ? t(afterCls) : "-",
      });
    }
    if (item.suggestedForDeletion || item.deleteSuggestionComment) {
      sections.push({
        title: t("Deletion"),
        before:
          item.cls || item.reviewCls ? t(item.cls || item.reviewCls) : "-",
        after: item.deletedAt ? t("Deleted") : "-",
      });
    }
    if (item.restoredAt) {
      sections.push({
        title: t("Restoration"),
        before: t("Deleted"),
        after: t("Restored"),
      });
    }
    if (item.comment != null && item.comment !== "") {
      sections.push({
        title: t("Commentaire"),
        commentOnly: true,
        text: item.comment,
      });
    }
    if (sections.length === 0) return null;
    return (
      <div className="bg-white rounded-lg border border-gray-200 py-3 px-4 min-w-[240px]">
        {sections.map((section, i) => (
          <div key={section.title}>
            {i > 0 && (
              <hr className="border-gray-200 my-2" aria-hidden="true" />
            )}
            <div className="font-semibold text-black mb-1">{section.title}</div>
            {section.commentOnly ? (
              <div className="text-sm text-black">{section.text}</div>
            ) : (
              <div className="text-sm text-black space-y-1">
                <div className="flex flex-col gap-1">
                  <span className="underline">{t("Before")}</span>
                  <span>{section.before}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="underline">{t("After")}</span>
                  <span>
                    {section.after}
                    {section.afterComment != null &&
                      section.afterComment !== "" &&
                      ` - ${section.afterComment}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );
  const viewMode = Cookies.get("viewMode") || "client";

  useEffect(() => {
    const fetchRevisionData = async () => {
      setIsLoading(true);
      try {
        const childContainers = superContainer
          ? getSuperContainerChildContainers(superContainer, containers)
          : [];

        const captureResults = await Promise.all(
          (historyMaterial || []).map(async (historyEntry) => {
            const time = historyEntry?._time;
            if (!time) return [];

            if (childContainers.length > 0) {
              return fetchRevisionsForSuperContainerCapture({
                azureUrl,
                historyEntry,
                childContainers,
              });
            }

            const targetContainer = superContainer || container;
            const containerId = getContainerMeasurementId(targetContainer);
            if (!containerId) return [];

            return fetchRevisionsForCapture({
              azureUrl,
              time,
              imageUrls: historyEntry.image_urls,
              fallbackImageUrl: getImageUrlFromHistoryItem(historyEntry),
              getMeasurementId: () => containerId,
            });
          }),
        );

        const mergedComments = captureResults.flat();
        setReviewComments(mergedComments);
      } catch (error) {
        console.error("Error fetching revision data:", error);
        setReviewComments([]);
      } finally {
        setIsLoading(false);
      }
    };
    if (historyMaterial && historyMaterial.length > 0) {
      fetchRevisionData();
    } else {
      setIsLoading(false);
    }
  }, [
    historyMaterial,
    container,
    superContainer,
    containers,
    azureUrl,
    refreshKey,
    refreshTrigger,
  ]);

  const processedData = useMemo(() => {
    return reviewComments.map((item) => {
      const modificationTypes = [];
      if (item.restoredAt) modificationTypes.push(t("Restoration"));
      if (item.reviewCls || (item.oldCls && item.oldCls !== item.cls)) {
        modificationTypes.push(t("Category"));
      }
      if (item.comment) modificationTypes.push(t("Commentaire"));
      if (item.suggestedForDeletion || item.deleteSuggestionComment) {
        modificationTypes.push(t("Deletion"));
      }

      let status = "pending";
      if (item.deletedAt) {
        status = "deleted";
      } else if (item.restoredAt) {
        status = "restored";
      } else if (item.reviewStatus === "accepted") {
        status = "validated";
      } else if (item.reviewStatus === "rejected") {
        status = "error";
      } else {
        status = "pending";
      }
      // deletedAt pour suppressions validées, sinon reviewTime, sinon date de la capture
      const date =
        item.deletedAt ||
        item.restoredAt ||
        item.reviewTime ||
        item._time ||
        item.captureTime ||
        "";
      return {
        ...item,
        modificationTypes: modificationTypes.join(", "),
        modificationTypeValues: modificationTypes,
        status,
        date,
      };
    });
  }, [reviewComments, t]);

  useEffect(() => {
    if (typeof onRevisionCountChange === "function") {
      onRevisionCountChange(processedData.length);
    }
  }, [processedData.length, onRevisionCountChange]);

  const filteredData = useMemo(() => {
    return processedData.filter((item) => {
      const statusMatch =
        statusFilter === "all" || item.status === statusFilter;
      const typeMatch =
        typeFilter === "all" ||
        (typeFilter === "category" &&
          (item.reviewCls || (item.oldCls && item.oldCls !== item.cls))) ||
        (typeFilter === "comment" && item.comment) ||
        (typeFilter === "deletion" &&
          (item.suggestedForDeletion || item.deleteSuggestionComment)) ||
        (typeFilter === "restoration" && item.restoredAt);
      return statusMatch && typeMatch;
    });
  }, [processedData, statusFilter, typeFilter]);

  const sortedData = useMemo(() => {
    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      let aValue, bValue;
      if (sortConfig.column === "date") {
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
      } else if (sortConfig.column === "status") {
        aValue = a.status;
        bValue = b.status;
      } else {
        return 0;
      }
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * recordPerPage;
    return sortedData.slice(startIndex, startIndex + recordPerPage);
  }, [sortedData, currentPage, recordPerPage]);
  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    setResultPage(Math.ceil(sortedData.length / recordPerPage));
  }, [sortedData.length, recordPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [recordPerPage]);

  if (isLoading) {
    return (
      <div className="w-full">
        <InlineLoader height="400px" />
      </div>
    );
  }
  const hasAnyRevisions = processedData.length > 0;
  const hasFilteredResults = sortedData.length > 0;
  const statusLabel =
    getStatusOptions(t).find((option) => option.value === statusFilter)
      ?.label ?? getStatusOptions(t)[0].label;
  const typeLabel =
    getTypeOptions(t).find((option) => option.value === typeFilter)?.label ??
    getTypeOptions(t)[0].label;

  if (!hasAnyRevisions) {
    return (
      <div className="w-full">
        <div className="table-wrapper table-wrapper--cols-3">
          <EmptyState
            title={t("No revisions yet")}
            description={t(
              "You can create revisions from the photo in the review tab.\nEach revision is subject to validation by our team.",
            )}
          />
        </div>
      </div>
    );
  }

  const getRevisionModalContext = () => {
    const containerId = selectedRevisionItem?.containerId;
    const image_url = selectedRevisionItem?.image_url;
    const time = selectedRevisionItem?.captureTime;

    if (!containerId || !image_url || !time) return null;

    const blobName = getOutputSurfaceBlobNameFromImageUrl(
      image_url,
      containerId,
    );

    return blobName
      ? { containerId, image_url, blobName, captureTime: time }
      : null;
  };

  const fetchOutputSurfaceBlob = async () => {
    const ctx = getRevisionModalContext();
    if (!ctx) {
      throw new Error("Missing container or image data");
    }

    const outputSurfaceData = await getAzureBlob({
      blobUrl: formatAzureBlob(
        ctx.image_url
          .replace("pictures", "output-surface")
          .replace(/-R\.jpeg|-L\.jpeg/, `-${ctx.containerId}-surface.json`),
      ),
    });

    if (outputSurfaceData.status !== 200 || !outputSurfaceData.data) {
      throw new Error(
        outputSurfaceData.data?.error || "Failed to load surface data",
      );
    }
    return { data: outputSurfaceData.data, blobName: ctx.blobName };
  };

  const handleDeleteSegmentation = async (polygonId) => {
    const ctx = getRevisionModalContext();
    if (!ctx) {
      showErrorToast(
        new Error("Missing container or image data"),
        t("Error deleting segmentation"),
        t,
      );
      return;
    }
    setIsRevisionModalLoading(true);
    try {
      await deleteSegmentation({
        blobName: ctx.blobName,
        segmentationId: polygonId,
        captureTime: ctx.captureTime,
      });
      showSuccessToast(t("Segmentation deleted successfully"), t);
      setRefreshTrigger((prev) => prev + 1);
      setIsRevisionModalOpen(false);
      setSelectedRevisionItem(null);
    } catch (error) {
      console.error("Error deleting segmentation:", error);
      showErrorToast(error, t("Error deleting segmentation"), t);
    } finally {
      setIsRevisionModalLoading(false);
    }
  };

  const handleDeleteSuggestion = async (
    polygonId,
    comment = "",
    options = {},
  ) => {
    const { accepted: deletionAccepted = false, reviewRequestType = "delete" } =
      options;
    const ctx = getRevisionModalContext();
    if (!ctx) {
      showErrorToast(
        new Error("Missing container or image data"),
        t("Error"),
        t,
      );
      throw new Error("Missing container or image data");
    }
    setIsRevisionModalLoading(true);
    try {
      const { data: outputSurfaceData, blobName } =
        await fetchOutputSurfaceBlob();
      const segmentations = outputSurfaceData.object_segmentations || [];
      const now = new Date().toISOString();
      const updatedList = segmentations.map((seg) =>
        seg.id === polygonId
          ? {
              ...seg,
              comment: comment || seg.comment,
              suggestedForDeletion: true,
              deleteSuggestionComment: comment || seg.deleteSuggestionComment,
              deleteSuggestionTime: now,
              reviewTime: now,
              reviewRequestType,
              reviewStatus: deletionAccepted ? seg.reviewStatus : "pending",
              ...(deletionAccepted && { deletionAccepted: true }),
            }
          : seg,
      );
      await updateOutputSurface({
        data: {
          ...outputSurfaceData,
          object_segmentations: updatedList,
          anomalies: updatedList.length,
        },
        blobName,
        captureTime: ctx.captureTime,
      });
      if (deletionAccepted) {
        await deleteSegmentation({
          blobName,
          segmentationId: polygonId,
          captureTime: ctx.captureTime,
        });
      }
      showSuccessToast(
        deletionAccepted
          ? t("Segmentation deleted successfully")
          : t("Deletion suggestion submitted"),
        t,
      );
      setRefreshTrigger((prev) => prev + 1);
      setIsRevisionModalOpen(false);
      setSelectedRevisionItem(null);
    } catch (error) {
      showErrorToast(
        error,
        deletionAccepted
          ? t("Error deleting segmentation")
          : t("Error submitting deletion suggestion"),
        t,
      );
      throw error;
    } finally {
      setIsRevisionModalLoading(false);
    }
  };

  const handleObjectSegmentation = async (
    polygonId,
    comment,
    reviewCls,
    type,
    reviewRequestType = "category",
    options = {},
  ) => {
    const ctx = getRevisionModalContext();
    if (!ctx) {
      showErrorToast(
        new Error("Missing container or image data"),
        t("Error"),
        t,
      );
      throw new Error("Missing container or image data");
    }
    setIsRevisionModalLoading(true);
    try {
      const { data: outputSurfaceData, blobName } =
        await fetchOutputSurfaceBlob();
      const segmentations = outputSurfaceData.object_segmentations || [];
      const authorizedMaterials =
        outputSurfaceData.waste_category_authorized_materials || [];
      const isAuthorizedMaterial = (cls) =>
        authorizedMaterials.some((m) => m.value === cls);

      let updatedList = null;
      let sourceObject = options.sourceObject || null;
      if (type === "client_review") {
        updatedList = segmentations.map((item) =>
          item.id === polygonId
            ? {
                ...item,
                comment,
                reviewCls,
                reviewTime: new Date().toISOString(),
                reviewRequestType,
                reviewStatus: "pending",
                oldCls: item.cls,
                originalCls: item.cls,
              }
            : item,
        );
      } else if (type === "validation") {
        // Admin: apply category directly — no reviewCls (not a correction request).
        // Keep existing reviewTime so validation does not overwrite the client request date.
        updatedList = segmentations.map((item) =>
          item.id === polygonId
            ? {
                ...item,
                comment,
                reviewCls: null,
                reviewTime: item.reviewTime,
                reviewRequestType,
                cls: reviewCls,
                originalCls: reviewCls,
                color: formattedColorMaterial(reviewCls),
                reviewStatus: "accepted",
                oldCls: item.cls,
                isAuthorizedMaterial: isAuthorizedMaterial(reviewCls),
              }
            : item,
        );
      }
      if (!updatedList) return;

      sourceObject =
        sourceObject ||
        updatedList.find((item) => item.id === polygonId) ||
        null;

      await updateOutputSurface({
        data: {
          ...outputSurfaceData,
          object_segmentations: updatedList,
          anomalies: updatedList.length,
        },
        blobName,
        captureTime: ctx.captureTime,
      });

      if (
        options.propagate &&
        sourceObject &&
        reviewRequestType === "category"
      ) {
        try {
          await propagateCategoryChange({
            containerId: ctx.containerId,
            captureTime: ctx.captureTime,
            sourceObject,
            trackObjectId: sourceObject.trackObjectId,
            firstAppearanceAt: sourceObject.firstAppearanceAt,
            mode: type === "validation" ? "applied" : "pending",
            newCls: reviewCls,
            color: formattedColorMaterial(reviewCls),
            comment,
            reviewRequestType,
            isAuthorizedMaterial: isAuthorizedMaterial(reviewCls),
            skipSource: true,
          });
        } catch (propagateError) {
          console.error("Category propagation failed:", propagateError);
        }
      }

      showSuccessToast(
        type === "client_review"
          ? t("correctionRequestTaken")
          : t("Modification validated"),
        t,
      );
      setRefreshTrigger((prev) => prev + 1);
      setIsRevisionModalOpen(false);
      setSelectedRevisionItem(null);
    } catch (error) {
      showErrorToast(
        error,
        type === "validation"
          ? t("Error validating modification")
          : t("Error submitting correction"),
        t,
      );
      throw error;
    } finally {
      setIsRevisionModalLoading(false);
    }
  };

  const handleSendReport = async () => {
    const safeReviewComments = Array.isArray(reviewComments)
      ? reviewComments
      : [];
    if (safeReviewComments.length === 0) return;

    const containerId = container?._id || superContainer?._id;
    if (!containerId) {
      showErrorToast(
        new Error("Missing containerId"),
        t("Error sending the email"),
        t,
      );
      return;
    }

    setIsSendingReport(true);
    try {
      const dataReviewPrepared = await Promise.all(
        safeReviewComments.map(async (review) => {
          const stage = stageRefs.current?.[getRevisionRowKey(review)];
          if (!stage || typeof stage.toDataURL !== "function") return null;

          const dataURL = stage.toDataURL({ pixelRatio: 2 });

          const afterCls = review.reviewCls || review.cls;
          const reviewLabel = review?.oldCls
            ? `${t(review.oldCls)} -> ${t(afterCls)}`
            : `${t(review.cls)} -> ${t(afterCls)}`;

          const time =
            review?.reviewTime || review?.date || review?.captureTime || "";

          return {
            blob: dataURL,
            review: reviewLabel,
            comment: review?.comment,
            time: `${formattedDate(time, t)} - ${formattedTime(time)}`,
            reviewStatus: t(review?.reviewStatus),
          };
        }),
      );

      const dataReview = dataReviewPrepared.filter(Boolean);
      if (dataReview.length === 0) {
        showErrorToast(
          new Error("No review images available for email"),
          t("Error sending the email"),
          t,
        );
        return;
      }
      await sendReviewReport({ dataReview, containerId });
      showSuccessToast(t("Message sent successfully, check your mail!"), t);
    } catch (error) {
      console.error("Erreur lors de l'envoi du mail :", error);
      showErrorToast(error, t("Error sending the email"), t);
    } finally {
      setIsSendingReport(false);
    }
  };

  return (
    <div className="w-full">
      <Card className="table">
        <StandardTable
          fluidColCount={3}
          fixedColWidth={0}
          columnAlignments={["left", "left", "left"]}
          topBar={
            <section className="table-standard-top-bar">
              <div className="table-standard-top-bar-inner">
                <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsStatusModalOpen(true);
                    }}
                  >
                    <span>
                      {t("Status")} : {statusLabel}
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsTypeModalOpen(true);
                    }}
                  >
                    <span>
                      {t("Type")} : {typeLabel}
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                </div>
                {isAkanthasAccount &&
                  viewMode === "admin" &&
                  reviewComments.length > 0 && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        className={`create-button flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#00A7A6] rounded-full ${
                          isSendingReport ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                        disabled={isSendingReport}
                        onClick={() => setIsSendReportModalOpen(true)}
                      >
                        {t("Send report by email")}
                      </button>
                    </div>
                  )}
              </div>
            </section>
          }
          header={
            <div className="table-standard-header">
              <TableHeaderCell
                label={t("Revision date")}
                columnKey="date"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <TableHeaderCell
                label={t("Status")}
                columnKey="status"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <div className="table-standard-header-cell table-standard-cell-align-left">
                {t("Modification type")}
              </div>
            </div>
          }
          bottom={
            <div className="table-standard-bottom table-pagination-bottom">
              <Pagination
                totalEntry={sortedData.length}
                recordPerPage={recordPerPage}
                setRecordPerPage={setRecordPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                resultPage={resultPage}
                setResultPage={setResultPage}
              />
            </div>
          }
        >
          {hasFilteredResults ? (
            paginatedData.map((item, index) => {
              const rowKey = getRevisionRowKey(item, index);
              return (
                <div key={rowKey} className="table-standard-row">
                  <div className="table-standard-row-cell">
                    <div className="flex items-center gap-4 min-w-0">
                      {item.image_url && item.points_konva && (
                        <div
                          role="button"
                          tabIndex={0}
                          className="w-[120px] h-[120px] rounded-lg overflow-hidden border border-[#d3d3d3] flex-shrink-0 flex items-center justify-center bg-[#f6f6f6] cursor-pointer"
                          onClick={() => {
                            setSelectedRevisionItem(item);
                            setIsRevisionModalOpen(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedRevisionItem(item);
                              setIsRevisionModalOpen(true);
                            }
                          }}
                        >
                          <WasteToolbarThumbnail
                            key={rowKey}
                            ref={(el) => {
                              if (el) {
                                stageRefs.current[getRevisionRowKey(item)] = el;
                              }
                            }}
                            subregion={item}
                            image_url={item.image_url}
                          />
                        </div>
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="font-semibold">
                          {formatDateOnly(item.date)}
                        </span>
                        <span className="table-standard-row-cell-truncate text-[13px] text-[#9CA3AF] font-normal">
                          {item.date
                            ? new Date(item.date).toLocaleTimeString("fr-FR", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className="table-standard-row-cell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item.deletedAt ? (
                      <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium char-badge char-badge-error">
                        <i
                          className="fas fa-trash text-xs text-current"
                          aria-hidden="true"
                        />
                        <span>{t("Deleted")}</span>
                      </span>
                    ) : item.restoredAt ? (
                      <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium char-badge char-badge-validated">
                        <i
                          className="fas fa-check text-xs text-current"
                          aria-hidden="true"
                        />
                        <span>{t("Restored")}</span>
                      </span>
                    ) : (
                      <StatusPicker
                        key={rowKey}
                        value={
                          item.reviewStatus === "accepted"
                            ? "accepted"
                            : item.reviewStatus === "rejected"
                              ? "rejected"
                              : "pending"
                        }
                        review={item}
                        data={superContainer || container}
                        time={item.captureTime}
                        setLoading={setIsStatusLoading}
                        isAkanthasAccount={isAkanthasAccount}
                        viewMode={viewMode}
                        reviewComments={reviewComments}
                        setReviewComments={setReviewComments}
                      />
                    )}
                  </div>
                  <div
                    className="table-standard-row-cell table-standard-cell-align-left relative overflow-visible"
                    ref={
                      openModificationDropdownIndex === index
                        ? modificationDropdownRef
                        : undefined
                    }
                  >
                    <div
                      className="flex items-center gap-2 min-w-0 cursor-pointer"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.modificationTypes) {
                          setOpenModificationDropdownIndex((prev) =>
                            prev === index ? null : index,
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          item.modificationTypes &&
                          (e.key === "Enter" || e.key === " ")
                        ) {
                          e.preventDefault();
                          setOpenModificationDropdownIndex((prev) =>
                            prev === index ? null : index,
                          );
                        }
                      }}
                      role={item.modificationTypes ? "button" : undefined}
                      tabIndex={item.modificationTypes ? 0 : undefined}
                    >
                      <span className="table-standard-row-cell-truncate">
                        {item.modificationTypes || "-"}
                      </span>
                      {item.modificationTypes && (
                        <i
                          className={`fas fa-chevron-down text-[#5e5e5e] text-xs transition-transform ${
                            openModificationDropdownIndex === index
                              ? "rotate-180"
                              : ""
                          }`}
                          aria-hidden
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="table-standard-row table-standard-row--empty">
              <div
                className="table-standard-row-cell"
                style={{ gridColumn: "1 / -1" }}
              >
                {t("No results match your search")}
              </div>
            </div>
          )}
        </StandardTable>
      </Card>
      {openModificationDropdownIndex != null &&
        dropdownPosition != null &&
        (() => {
          const item = paginatedData[openModificationDropdownIndex];
          if (!item?.modificationTypes) return null;
          const content = renderModificationDropdownContent(item);
          if (!content) return null;
          return createPortal(
            <div
              ref={modificationDropdownPortalRef}
              className="fixed z-[9999]"
              style={{
                left: dropdownPosition.left,
                bottom: dropdownPosition.bottom,
              }}
            >
              {content}
            </div>,
            document.body,
          );
        })()}
      <StatusFilterModalRevision
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onApply={(value) => {
          setStatusFilter(value);
          setCurrentPage(1);
        }}
        currentStatusFilter={statusFilter}
      />
      <TypeFilterModalRevision
        isOpen={isTypeModalOpen}
        onClose={() => setIsTypeModalOpen(false)}
        onApply={(value) => {
          setTypeFilter(value);
          setCurrentPage(1);
        }}
        currentTypeFilter={typeFilter}
      />
      <Modal
        isOpen={isSendReportModalOpen}
        toggle={() => setIsSendReportModalOpen(false)}
        contentClassName="rounded-lg"
        style={{ maxWidth: "520px" }}
        centered
      >
        <ModalHeader className="flex justify-between items-center pb-3 border-0">
          <span className="text-gray-900 font-semibold text-base">
            {t("Send report by email")}
          </span>
          <CloseIcon
            style={{
              color: "#6B7280",
              cursor: "pointer",
              fontSize: "20px",
            }}
            onClick={() => setIsSendReportModalOpen(false)}
          />
        </ModalHeader>
        <ModalBody className="px-6 pt-0">
          <p className="text-sm text-gray-700 mb-0">
            {t("Are you sure you want to send the report by email?")}
          </p>
        </ModalBody>
        <ModalFooter className="flex justify-end gap-2 pt-4 pb-4 border-0 px-6">
          <button
            type="button"
            onClick={() => setIsSendReportModalOpen(false)}
            className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium"
            disabled={isSendingReport}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={async () => {
              await handleSendReport();
              setIsSendReportModalOpen(false);
            }}
            disabled={isSendingReport}
            className="px-4 py-2 bg-[#2AB9B8] text-white rounded-full hover:bg-[#009493] disabled:opacity-50 text-sm font-medium"
          >
            {isSendingReport ? t("Sending...") : t("Confirm")}
          </button>
        </ModalFooter>
      </Modal>
      {selectedRevisionItem && (
        <WastePolygonModal
          isModalView={isRevisionModalOpen}
          setIsModalView={(value) => {
            const next = value !== undefined ? value : !isRevisionModalOpen;
            setIsRevisionModalOpen(next);
            if (!next) setSelectedRevisionItem(null);
          }}
          selectedAnomalie={selectedRevisionItem}
          imageUrl={selectedRevisionItem.image_url}
          captureTime={selectedRevisionItem.captureTime}
          containerId={selectedRevisionItem.containerId}
          imgDivWidth={550}
          imgDivHeight={350}
          imgWidth={2000}
          imgHeight={2000}
          handleObjectSegmentation={handleObjectSegmentation}
          handleDeleteSegmentation={handleDeleteSegmentation}
          handleDeleteSuggestion={handleDeleteSuggestion}
          onRestore={async (segmentationId) => {
            const time = selectedRevisionItem.captureTime;
            const containerId = selectedRevisionItem.containerId;
            if (!selectedRevisionItem.image_url || !time || !containerId) {
              throw new Error("Missing data for restore");
            }
            const blobName =
              selectedRevisionItem.sourceBlobName ||
              getOutputSurfaceBlobNameFromImageUrl(
                selectedRevisionItem.image_url,
                containerId,
              );
            try {
              await restoreSegmentation({
                blobName,
                segmentationId,
                captureTime: time,
              });
              showSuccessToast(t("Segmentation restored successfully"), t);
              setRefreshTrigger((prev) => prev + 1);
              setIsRevisionModalOpen(false);
              setSelectedRevisionItem(null);
            } catch (error) {
              showErrorToast(error, t("Error restoring segmentation"), t);
              throw error;
            }
          }}
          isLoading={isRevisionModalLoading}
        />
      )}
      {isStatusLoading && <LoadingOverlay />}
    </div>
  );
};
export default CharacterizationRevisionHistory;
